import { lazy, Suspense } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Composer } from '../components/Composer'
import '../styles/landing-ink.css'

// Lazy: three.js (~128 kB gz) never rides in the main bundle. While it
// loads, the atmo blooms + ghost wordmark carry the backdrop.
const HeroWorld = lazy(() => import('../components/HeroWorld'))

// Starting points + template rail. Both seed the Composer through the same
// templateSeed contract TemplatesPage uses (Composer applies late seeds).
const CHIPS = [
  { id: 'chip-mv', label: 'Music video', prompt: 'Make a music video for my song — pick a visual world, keep one consistent character, cut on the beat...' },
  { id: 'chip-ad', label: 'Ad', prompt: 'Make a 15-second ad for my product — strong hook in the first second, punchy captions, end on the logo...' },
  { id: 'chip-anime', label: 'Anime', prompt: 'Make an anime short — high-quality animation, golden-hour light, a quiet emotional story about...' },
  { id: 'chip-drama', label: 'Short drama', prompt: 'Make a short drama episode — two characters, one location, a secret revealed in the last shot...' },
  { id: 'chip-trailer', label: 'Trailer', prompt: 'Make a cinematic trailer — slow build, hard cuts at the drop, title cards that tease...' },
]

const HERO_TEMPLATES = [
  { id: 'tpl-anime-mv', kicker: 'Music video', label: 'Anime MV', poster: '/showcase/anime-mv.jpg', video: '/showcase/anime-mv.mp4',
    prompt: 'Make an anime music video for my song — golden-hour light, one consistent character, slow cinematic cuts on the beat...' },
  { id: 'tpl-city', kicker: 'Music video', label: 'Neon city night', poster: '/showcase/city-night.jpg', video: '/showcase/city-night.mp4',
    prompt: 'Cut a moody neon night MV — rain-slick streets, sodium light, slow push-ins, cuts landing on the beat...' },
  { id: 'tpl-bball', kicker: 'Hype reel', label: 'Manga sports edit', poster: '/showcase/bball-anime.jpg', video: '/showcase/bball-anime.mp4',
    prompt: 'Turn the game into a manga-style hype reel — speed lines, impact frames, hard cuts on the biggest plays...' },
  { id: 'tpl-theatre', kicker: 'Trailer', label: 'Cinematic teaser', poster: '/showcase/theatre.jpg', video: '/showcase/theatre.mp4',
    prompt: 'Make a cinematic teaser — photoreal, slow build, hard cuts at the drop, title cards that tease the story...' },
]

function consoleBase(): string {
  const explicit = import.meta.env.VITE_CONSOLE_BASE as string | undefined
  if (explicit) return explicit
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:5174'
  }
  return 'https://ponpon.app'
}

export function LandingPage() {
  const navigate = useNavigate()
  // Touch devices have no hover: template previews autoplay there instead
  // (tiny muted loops, ~1-2 MB each). Desktop keeps hover-to-preview.
  const coarse =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: coarse)').matches
  const seedComposer = (prompt: string, templateId: string) => {
    navigate('/', { state: { templateSeed: { prompt, aspectRatio: '16:9', templateId } } })
  }
  return (
    <div className="landing-ink">
      {/* haevi ink world, back-to-front: atmo blooms → ghost wordmark →
          three.js aurora ribbons → vignette → content (z-1). */}
      <div className="background-glow" aria-hidden="true" />
      <div className="ghost-wordmark" aria-hidden="true"><span>haevi</span></div>
      <Suspense fallback={null}>
        <HeroWorld />
      </Suspense>
      <div className="landing-vignette" aria-hidden="true" />

    <section className="relative z-[1] max-w-6xl mx-auto px-5 sm:px-8 pt-20 pb-24">
      {/* Top eyebrow */}
      <div className="flex justify-center mb-8 anim-rise-soft" style={{ animationDelay: '40ms' }}>
        <span className="hero-eyebrow">General-purpose video agent</span>
      </div>

      {/* Hero — the brand face, one line */}
      <div className="text-center max-w-4xl mx-auto">
        <h1
          className="hero-h1 anim-rise text-[clamp(3rem,8vw,6.5rem)]"
          style={{ animationDelay: '120ms' }}
        >
          Direct anything.
        </h1>

        <p
          className="hero-sub mt-7 text-[1.2rem] sm:text-[1.32rem] leading-snug max-w-2xl mx-auto anim-rise-soft"
          style={{ animationDelay: '380ms' }}
        >
          haevi is a general-purpose video agent. Music videos, ads, anime,
          trailers, short drama — generated, cut, and scored from a single
          prompt. Recut any panel. Ship 9:16 or 16:9.
        </p>
      </div>

      {/* Composer / CTA — always rendered. The Composer itself decides
          what to do on submit based on auth state: signed-in users go
          straight to createProject + workspace; signed-out users get
          their prompt stashed in sessionStorage and are bounced to
          /login. After auth completes, the Composer auto-resumes via
          the pending-compose stash so the user lands in the workspace
          with their project already created — they never re-type the
          prompt. See Composer.tsx PENDING_COMPOSE_KEY for the contract. */}
      <div className="mt-14 anim-rise-soft" style={{ animationDelay: '540ms' }}>
        <Composer />
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[0.86rem] text-text-3">
          <Link to="/dashboard" className="hover:text-text transition-colors">Workspace</Link>
          <span className="text-text-4">·</span>
          <Link to="/templates" className="hover:text-text transition-colors">Templates</Link>
          <span className="text-text-4">·</span>
          <a href={`${consoleBase()}/dashboard`} className="hover:text-text transition-colors">
            Canvas editor (legacy) →
          </a>
        </div>

        {/* Starting-point chips — one tap pre-fills a concrete, editable ask */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2" data-testid="starting-points">
          {CHIPS.map((c) => (
            <button key={c.id} type="button" className="starting-chip" onClick={() => seedComposer(c.prompt, c.label)}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Template rail — real output as starting points; hover previews */}
        <div className="template-rail-title mt-10">Start from a template</div>
        <div className="template-rail mt-4" data-testid="template-rail">
          {HERO_TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              className="template-card"
              style={{ backgroundImage: `url(${t.poster})` }}
              onClick={() => seedComposer(t.prompt, t.label)}
              onMouseEnter={(e) => { void e.currentTarget.querySelector('video')?.play() }}
              onMouseLeave={(e) => { e.currentTarget.querySelector('video')?.pause() }}
              aria-label={`Use template: ${t.label}`}
            >
              <video
                src={t.video}
                muted
                loop
                playsInline
                autoPlay={coarse}
                preload={coarse ? 'metadata' : 'none'}
                style={coarse ? { opacity: 1 } : undefined}
              />
              <span className="template-kicker">{t.kicker}</span>
              <span className="template-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Capability strip — modern feature row */}
      <div className="mt-32">
        <div className="flex items-center gap-3 mb-8">
          <span className="t-mono-caps">Capabilities</span>
          <span className="flex-1 h-px bg-line" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Capability
            no="01"
            title="Story"
            body="Prompt expands into characters, locations, chapters, and a shot board."
            accent="A first draft in ~60s."
          />
          <Capability
            no="02"
            title="Stage"
            body="Panel-level recutting. Per-shot motion. Recast a face without reshooting the scene."
            accent="Surgical edits."
          />
          <Capability
            no="03"
            title="Ship"
            body="Vertical for shorts. Wide for screen. Native aspects, exportable frames."
            accent="Post-ready."
          />
        </div>
      </div>

      {/* Quiet pull-quote */}
      <div className="mt-32 max-w-3xl mx-auto text-center">
        <p className="h-italic text-[1.8rem] sm:text-[2.1rem] leading-snug text-text-2">
          “The studio you wished you could afford —
          <span className="text-text"> answering in chat.</span>”
        </p>
        <div className="mt-4 t-mono-caps">— prompt-first cinema, v2</div>
      </div>
    </section>
    </div>
  )
}

function Capability({ no, title, body, accent }: { no: string; title: string; body: string; accent: string }) {
  return (
    <div className="card card-hover p-6 group">
      <div className="flex items-baseline justify-between mb-3">
        <span className="t-mono-caps">{no} · {title}</span>
        <span className="live-dot opacity-60 group-hover:opacity-100 transition-opacity" />
      </div>
      <p className="text-[1.02rem] leading-relaxed text-text-2 mb-4">{body}</p>
      <p className="h-italic text-text text-[1.05rem]">{accent}</p>
    </div>
  )
}
